import { z } from 'zod'
import { timestampSchema } from './common.js'
import { actorRefSchema, dispatchTargetTypeSchema } from './dispatch.js'
import { paginationQuerySchema } from './pagination.js'

// Append-only dispatch activity log. One row per job-number search, per
// recommended officer/job shown, and per outcome mark/un-mark. This is the
// analytics source of truth; dispatchOutcomes remains the live UI state.

export const dispatchActivityEventTypeSchema = z.enum([
  'search', // a job number was searched (RRP-0026)
  'recommendation', // a nearby officer/job was shown (RRP-0027)
  'outcome', // a target was marked called/accepted/declined or cleared (RRP-0028/0029)
])
export type DispatchActivityEventType = z.infer<typeof dispatchActivityEventTypeSchema>

export const dispatchActivityOutcomeStatusSchema = z.enum([
  'call_attempted',
  'accepted',
  'declined',
  'cleared', // an un-mark (inverse of call_attempted)
])
export type DispatchActivityOutcomeStatus = z.infer<typeof dispatchActivityOutcomeStatusSchema>

// JSON-serializable extras (distanceMiles, durationSeconds, lastSeen, postcode,
// assetNumber, acceptedJob…). Open but serializable so the RPC return type
// stays serializable, matching dispatchLookupSchema.request.
const detailSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
)

export const dispatchActivityEventSchema = z.object({
  id: z.string(),
  sessionId: z.string().nullable(),
  eventType: dispatchActivityEventTypeSchema,
  targetType: dispatchTargetTypeSchema.nullable(),
  targetKey: z.string().nullable(),
  label: z.string().nullable(),
  outcomeStatus: dispatchActivityOutcomeStatusSchema.nullable(),
  jobNumber: z.string().nullable(),
  detail: detailSchema,
  actor: actorRefSchema.nullable(),
  createdAt: timestampSchema,
})
export type DispatchActivityEvent = z.infer<typeof dispatchActivityEventSchema>

// Shared ceiling for "how many activity rows at once": the query's max
// pageSize and the web table's DOM row cap are the same number for the same
// reason (an unbounded render/response), so they read off this one constant
// instead of two independently-maintained literals.
export const ACTIVITY_LIST_ROW_CAP = 500

// Raw-events list query: pagination + optional filters. Dates are ISO; filters
// validate against the enums so a bad ?eventType is a 400.
export const dispatchActivityQuerySchema = paginationQuerySchema.extend({
  eventType: dispatchActivityEventTypeSchema.optional(),
  targetType: dispatchTargetTypeSchema.optional(),
  sessionId: z.string().uuid().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  // The activity feed is the one list that legitimately renders a wide page, so
  // the ceiling is raised here rather than in paginationQuerySchema, which the
  // users/roles/tokens lists also use.
  pageSize: z.coerce.number().int().min(1).max(ACTIVITY_LIST_ROW_CAP).default(50),
  // 'csv' switches the activity route to a text/csv download (RRP-0031);
  // 'json' (default) keeps the paginated JSON envelope.
  format: z.enum(['json', 'csv']).default('json'),
  // CSV-only row bound: absent = ACTIVITY_EXPORT_CAP, a number = that bound,
  // 'all' = uncapped. The coercing number comes first because 'all' coerces to
  // NaN and falls through to the literal, while '500' parses as 500.
  limit: z.union([z.coerce.number().int().min(1), z.literal('all')]).optional(),
})
export type DispatchActivityQuery = z.infer<typeof dispatchActivityQuerySchema>
